import axios from 'axios';
import { useAuthStore } from '../stores/authStore';
import { LOCAL_FIRST } from './powersync/db';
import * as local from './powersync/rundownLocal';
import { isDesktop, agent } from './desktop';

const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '/api',
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = useAuthStore.getState().token;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// A 403 with code WORKSPACE_ACCESS means the open workspace is no longer
// reachable: the person was removed from the account, their guest access ended,
// or the workspace was deleted. Re-read the session — the server lands them in a
// workspace they can still open, or none — and leave any page that was about the
// old one. Several requests fail together, so one refresh serves them all.
let refreshingSession = null;
function refreshSessionAfterLostAccess() {
  if (refreshingSession) return refreshingSession;
  refreshingSession = api.get('/auth/me')
    .then((res) => {
      const { token, ...user } = res.data;
      useAuthStore.getState().setAuth(token, user);
      if (window.location.pathname !== '/') window.location.href = '/';
    })
    .catch(() => {})
    .finally(() => {
      setTimeout(() => { refreshingSession = null; }, 2000);
    });
  return refreshingSession;
}

// Response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid — log out
      useAuthStore.getState().logout();
      window.location.href = '/login';
    } else if (error.response?.status === 403 && error.response?.data?.code === 'WORKSPACE_ACCESS') {
      refreshSessionAfterLostAccess();
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  // inviteToken: sign up to accept an invite instead of opening a new account.
  register: (email, password, workspaceName, inviteToken) =>
    api.post('/auth/register', { email, password, workspaceName, inviteToken }),
  me: () => api.get('/auth/me'),
};

// Workspace API — the open workspace, switching, and its guests
export const workspaceAPI = {
  get: () => api.get('/workspaces/my'),
  rename: (name) => api.patch('/workspaces/my', { name }),
  mine: () => api.get('/workspaces/mine'),
  switch: (workspace_id) => api.post('/workspaces/switch', { workspace_id }),
  guests: () => api.get('/workspaces/my/guests'),
  updateGuest: (userId, expiresAt) => api.patch(`/workspaces/my/guests/${userId}`, { expires_at: expiresAt }),
  removeGuest: (userId) => api.delete(`/workspaces/my/guests/${userId}`),
};

// Accounts (organisations): members, roles, seats and workspaces
export const organisationsAPI = {
  mine: () => api.get('/organisations/mine'),
  // Open an account of your own, on a trial. Returns a fresh session.
  create: (name) => api.post('/organisations', { name }),
  get: (id) => api.get(`/organisations/${id}`),
  rename: (id, name) => api.patch(`/organisations/${id}`, { name }),
  updateMemberRole: (id, userId, role) => api.patch(`/organisations/${id}/members/${userId}`, { role }),
  removeMember: (id, userId) => api.delete(`/organisations/${id}/members/${userId}`),
  createWorkspace: (id, name) => api.post(`/organisations/${id}/workspaces`, { name }),
  // Permanent: the workspace and every show in it.
  deleteWorkspace: (id, workspaceId) => api.delete(`/organisations/${id}/workspaces/${workspaceId}`),
};

// Invites. create() returns the link once; there is no email service, so the
// sender passes it on. lookup() is public, for the accept page before sign-in.
export const invitesAPI = {
  create: (body) => api.post('/invites', body),
  list: (params) => api.get('/invites', { params }),
  withdraw: (id) => api.delete(`/invites/${id}`),
  lookup: (token) => api.get(`/invites/lookup/${encodeURIComponent(token)}`),
  accept: (token) => api.post(`/invites/accept/${encodeURIComponent(token)}`),
};

// Shows API
export const showsAPI = {
  list: () => api.get('/shows'),
  create: (data) => api.post('/shows', data),
  // Editor load reads locally for instant open; the dashboard list, create and
  // settings writes stay on REST (server-side defaults, billing limits).
  // If the show isn't in the local store yet (replication lag, initial sync
  // still running, a stuck local DB, or a just-switched workspace), fall back
  // to the cloud so the editor always opens — local-first takes over once the
  // row syncs down (the editor's reactive watch fills it in).
  get: (id) => LOCAL_FIRST
    ? local.getShowWithCues(id).catch(e => (e?.localMiss ? api.get(`/shows/${id}`) : Promise.reject(e)))
    : api.get(`/shows/${id}`),
  update: (id, data) => api.patch(`/shows/${id}`, data),
  delete: (id) => api.delete(`/shows/${id}`),
  start: (id) => api.post(`/shows/${id}/start`),
  end: (id) => api.post(`/shows/${id}/end`),
  reset: (id) => api.post(`/shows/${id}/reset`),
  clone: (id) => api.post(`/shows/${id}/clone`),
  generateStreamdeckToken: (id) => api.post(`/shows/${id}/streamdeck-token`),
  // Returns the show's existing bridge token, minting one on first ask. Pass
  // regenerate to cut off bridges running on the old one.
  bridgeToken: (id, { regenerate = false } = {}) => api.post(`/shows/${id}/bridge-token`, { regenerate }),
  move: (id, targetWorkspaceId) => api.post(`/shows/${id}/move`, { target_workspace_id: targetWorkspaceId }),
  getUsers: (id) => api.get(`/shows/${id}/users`),
  addUser: (id, userId, role) => api.post(`/shows/${id}/users`, { user_id: userId, role }),
};

// Cues API
//
// When VITE_LOCAL_FIRST is on, the CRUD operations (list/create/update/delete/
// clone/reorder) run against the local PowerSync store for instant, offline
// reads/writes that sync to Neon in the background. The orchestration commands
// (take/skip/back/test) stay on REST — they drive vMix/Singular and the
// server-side show clock, which is not client data.
export const cuesAPI = {
  list: (showId) => LOCAL_FIRST ? local.listCues(showId) : api.get(`/shows/${showId}/cues`),
  create: (showId, data) => LOCAL_FIRST ? local.createCue(showId, data) : api.post(`/shows/${showId}/cues`, data),
  update: (showId, cueId, data) => LOCAL_FIRST ? local.updateCue(showId, cueId, data) : api.patch(`/shows/${showId}/cues/${cueId}`, data),
  delete: (showId, cueId) => LOCAL_FIRST ? local.deleteCue(showId, cueId) : api.delete(`/shows/${showId}/cues/${cueId}`),
  clone: (showId, cueId) => LOCAL_FIRST ? local.cloneCue(showId, cueId) : api.post(`/shows/${showId}/cues/${cueId}/clone`),
  bulkClone: (showId, cueIds, insertAfterId) => LOCAL_FIRST
    ? local.bulkCloneCues(showId, cueIds, insertAfterId)
    : api.post(`/shows/${showId}/cues/bulk-clone`, { cueIds, insertAfterId: insertAfterId ?? undefined }),
  reorder: (showId, orderedIds) => LOCAL_FIRST
    ? local.reorderCues(showId, orderedIds)
    : api.post(`/shows/${showId}/cues/reorder`, { orderedIds }, { timeout: 90000 }),
  take: (showId) => api.post(`/shows/${showId}/cues/take`),
  skip: (showId, cueId) => api.post(`/shows/${showId}/cues/skip`, cueId ? { cueId } : {}),
  back: (showId) => api.post(`/shows/${showId}/cues/back`),
  test: (showId, cueId) => api.post(`/shows/${showId}/cues/${cueId}/test`),
};

// vMix API
//
// On the desktop build the vMix path runs against the on-prem local agent over
// localhost (LAN access to vMix, no WAN). In the browser build it stays on the
// cloud REST/LocalBridge path. isDesktop is false in the browser build, so this
// branch is inert there.
export const vmixAPI = {
  status: (showId) => isDesktop
    ? Promise.resolve({ data: { connected: true } })
    : api.get('/vmix/status', { params: { show_id: showId } }),
  connect: (showId) => isDesktop
    ? agent.vmixConnect(showId).then((d) => ({ data: d }))
    : api.post('/vmix/connect', { show_id: showId }),
  disconnect: (showId) => isDesktop
    ? Promise.resolve({ data: {} })
    : api.post('/vmix/disconnect', { show_id: showId }),
  function: (showId, fn, input, value) => isDesktop
    ? agent.vmixFunction(showId, fn, input, value).then((d) => ({ data: d }))
    : api.post('/vmix/function', { show_id: showId, function: fn, input, value }),
  // Run script code on a vMix without installing it there (ScriptStartDynamic).
  // Always via the cloud/bridge: the Script Editor tool is not show-scoped, so
  // showId is only there to pick which LocalBridge relays the call.
  scriptRun: (showId, instanceId, code) =>
    api.post('/vmix/script-run', { show_id: showId, target_instance_id: instanceId || undefined, code }),
  // instanceId (optional) → that workspace instance's inputs; omit for Primary.
  inputs: (showId, instanceId) => isDesktop
    ? agent.vmixInputs(showId, instanceId).then((d) => ({ data: d }))
    : api.get('/vmix/inputs', { params: { show_id: showId, instance_id: instanceId || undefined } }),
};

// Workspace vMix instance registry (named host:port targets for cue actions)
export const vmixInstancesAPI = {
  list: () => api.get('/vmix-instances'),
  create: (data) => api.post('/vmix-instances', data),
  update: (id, data) => api.patch(`/vmix-instances/${id}`, data),
  remove: (id) => api.delete(`/vmix-instances/${id}`),
};

// The preset library. Only the <Scripting> block of a .vmix is stored — the
// rest of the file, including StreamingSettings and its stream keys, stays on
// the operator's machine. So there is no "download the preset" call here: the
// server hands back a scripting block and the browser splices it into the
// operator's own copy of the file.
export const vmixPresetsAPI = {
  list: () => api.get('/vmix-presets'),
  get: (id, { includeScripting = false } = {}) =>
    api.get(`/vmix-presets/${id}`, { params: includeScripting ? { include_scripting: 'true' } : {} }),
  upload: (file, name) => {
    const formData = new FormData();
    formData.append('file', file);
    if (name) formData.append('name', name);
    return api.post('/vmix-presets', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000,
    });
  },
  scripts: (id) => api.get(`/vmix-presets/${id}/scripts`),
  script: (id, ref) => api.get(`/vmix-presets/${id}/scripts/${encodeURIComponent(ref)}`),
  saveScripts: (id, scripts, note) =>
    api.put(`/vmix-presets/${id}/scripts`, { scripts, note }, { timeout: 120000 }),
  plan: (id, body) => api.post(`/vmix-presets/${id}/plan`, body, { timeout: 120000 }),
  versions: (id) => api.get(`/vmix-presets/${id}/versions`),
  revert: (id, versionId) => api.post(`/vmix-presets/${id}/revert`, { version_id: versionId }),
  remove: (id) => api.delete(`/vmix-presets/${id}`),
};

// Singular API
export const singularAPI = {
  debug: (showId) => api.get('/singular/debug', { params: { show_id: showId } }),
  // Whether a token is stored server-side and still authenticates. The client
  // cannot infer this: the key is redacted from REST and not synced at all.
  status: (showId) => api.get('/singular/status', { params: { show_id: showId } }),
  compositions: (showId) => api.get('/singular/compositions', { params: { show_id: showId } }),
  nodes: (showId, subCompositionId) => api.get('/singular/nodes', { params: { show_id: showId, sub_composition_id: subCompositionId } }),
  show: (showId, subCompositionId) => api.post('/singular/show', { show_id: showId, sub_composition_id: subCompositionId }),
  hide: (showId, subCompositionId) => api.post('/singular/hide', { show_id: showId, sub_composition_id: subCompositionId }),
  hideAll: (showId) => api.post('/singular/hide-all', { show_id: showId }),
  refresh: (showId) => api.post('/singular/refresh', { show_id: showId }),
  patch: (showId, body) => api.post('/singular/patch', { show_id: showId, body }),
  previewCompositions: (token) => api.post('/singular/preview/compositions', { token }),
  previewShow: (token, subCompositionId) => api.post('/singular/preview/show', { token, sub_composition_id: subCompositionId }),
  previewHide: (token, subCompositionId) => api.post('/singular/preview/hide', { token, sub_composition_id: subCompositionId }),
};

// Cue Blocks API
export const cueBlocksAPI = {
  list: () => api.get('/cue-blocks'),
  save: (name, showId, description, cueIds) => api.post('/cue-blocks', {
    name, show_id: showId, description,
    ...(cueIds && cueIds.length ? { cue_ids: cueIds } : {}),
  }),
  apply: (blockId, showId, insertAtOrder) => api.post(`/cue-blocks/${blockId}/apply/${showId}`, insertAtOrder != null ? { insertAtOrder } : {}),
  delete: (id) => api.delete(`/cue-blocks/${id}`),
};

// Import API
export const importAPI = {
  csv: (showId, file, clear = false) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/import/csv/${showId}?clear=${clear}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000,
    });
  },
  sheets: (showId, url, clear = false) => api.post(`/import/sheets/${showId}?clear=${clear}`, { url }, { timeout: 120000 }),
};

// Tools API
export const toolsAPI = {
  list: () => api.get('/tools'),
  presets: (toolKey) => api.get(`/tools/${toolKey}/presets`),
  createPreset: (toolKey, body) => api.post(`/tools/${toolKey}/presets`, body),
  updatePreset: (toolKey, presetId, body) => api.patch(`/tools/${toolKey}/presets/${presetId}`, body),
  deletePreset: (toolKey, presetId) => api.delete(`/tools/${toolKey}/presets/${presetId}`),
  runStandingsUpdater: (body) => api.post('/tools/standings-updater/run', body, {
    timeout: 90000,
    validateStatus: (s) => s >= 200 && s < 600,
  }),
  runs: () => api.get('/tools/standings-updater/runs'),
  runAthleteImporter: (body) => api.post('/tools/athlete-importer/run', body, {
    timeout: 60000,
    validateStatus: (s) => s >= 200 && s < 600,
  }),
  runSeriesStandings: (body) => api.post('/tools/series-standings/run', body, {
    timeout: 90000,
    validateStatus: (s) => s >= 200 && s < 600,
  }),
};

// ClimbAlong Data Manager.
//
// Unlike the other tools this one drives a long-running daemon rather than a
// single run, so the panel polls `status` on a 1 s timer while it is open. That
// endpoint reads in-memory bridge state and touches neither the database nor the
// network, so it is cheap enough to poll at that rate — the alternative would be
// a second realtime pattern for one page. The bridge also broadcasts
// `climbbridge:health` onto the show's WebSocket room, which is what a status
// pill elsewhere in the app should read.
export const climbBridgeAPI = {
  // Talks to ClimbAlong, so it gets a longer timeout than the default 15 s.
  resolve: (body) => api.post('/tools/climb-bridge/resolve', body, { timeout: 30000 }),
  start: (body) => api.post('/tools/climb-bridge/start', body, { timeout: 30000 }),
  stop: (showId) => api.post('/tools/climb-bridge/stop', { show_id: showId }),
  status: (showId) => api.get('/tools/climb-bridge/status', {
    params: showId ? { show_id: showId } : {},
    // A poll that arrives late is worthless — the next one is already due.
    timeout: 5000,
  }),
  state: (showId) => api.get('/tools/climb-bridge/state', { params: { show_id: showId } }),
  setOnAirLane: (showId, laneId) => api.post('/tools/climb-bridge/onair', { show_id: showId, lane_id: laneId }),
  selectAthlete: (showId, athleteId) => api.post('/tools/climb-bridge/athlete', { show_id: showId, athlete_id: athleteId }),
  autoFollow: (showId, enabled = true) => api.post('/tools/climb-bridge/athlete', { show_id: showId, athlete_id: null, auto_follow: enabled }),
  // The running order the production knows and ClimbAlong does not — see
  // mc_climb_bridge_running_order.
  setRunningOrder: (showId, laneId, athleteIds) =>
    api.post('/tools/climb-bridge/running-order', { show_id: showId, lane_id: laneId, athlete_ids: athleteIds }),
  // Omit folder to re-read the one already set; '' to stop using photos.
  reloadPhotos: (showId, folder) =>
    api.post('/tools/climb-bridge/photos', folder === undefined ? { show_id: showId } : { show_id: showId, folder }, { timeout: 30000 }),
  clearRunningOrder: (showId, laneId) =>
    api.post('/tools/climb-bridge/running-order', { show_id: showId, lane_id: laneId, clear: true }),
  updateConfig: (showId, body) => api.patch('/tools/climb-bridge/config', { show_id: showId, ...body }),
  upstream: (showId, params = {}) => api.get('/tools/climb-bridge/upstream', { params: { show_id: showId, ...params } }),
  recordings: () => api.get('/tools/climb-bridge/recordings'),
  renameRecording: (id, name) => api.patch(`/tools/climb-bridge/recordings/${id}`, { name }),
  deleteRecording: (id) => api.delete(`/tools/climb-bridge/recordings/${id}`),
  // Scanning a recording of several days takes a moment; trimming one can take longer.
  recordingActivity: (id) => api.get(`/tools/climb-bridge/recordings/${id}/activity`, { timeout: 180000 }),
  trimRecording: (id, body) => api.post(`/tools/climb-bridge/recordings/${id}/trim`, body, { timeout: 600000 }),
  // A whole recording as a file, to keep somewhere Mission Control cannot lose it.
  exportRecording: (id) => api.get(`/tools/climb-bridge/recordings/${id}/export`, { responseType: 'blob', timeout: 300000 }),
  importRecording: (file) => {
    const body = new FormData();
    body.append('file', file);
    return api.post('/tools/climb-bridge/recordings/import', body, { timeout: 300000 });
  },
  // Stopping waits for the last chunk to be stored, which can take a moment.
  startRecording: (showId, name) => api.post('/tools/climb-bridge/recording/start', { show_id: showId, ...(name ? { name } : {}) }),
  stopRecording: (showId) => api.post('/tools/climb-bridge/recording/stop', { show_id: showId }, { timeout: 30000 }),
  // Loading a long recording and stopping a live bridge first can both take a while.
  startPlayback: (body) => api.post('/tools/climb-bridge/playback/start', body, { timeout: 60000 }),
  controlPlayback: (showId, action, params = {}) => api.post('/tools/climb-bridge/playback/control', { show_id: showId, action, ...params }),
  runs: () => api.get('/tools/climb-bridge/runs'),
};

// The gym microsite (/gym): sessions are shared plans, runs are one person's workouts.
export const gymAPI = {
  sessions: () => api.get('/gym/sessions'),
  session: (id) => api.get(`/gym/sessions/${id}`),
  createSession: (body) => api.post('/gym/sessions', body),
  updateSession: (id, body) => api.patch(`/gym/sessions/${id}`, body),
  duplicateSession: (id, name) => api.post(`/gym/sessions/${id}/duplicate`, name ? { name } : {}),
  deleteSession: (id) => api.delete(`/gym/sessions/${id}`),
  orderSessions: (ids) => api.put('/gym/sessions/order', { ids }),
  run: (id) => api.get(`/gym/runs/${id}`),
  checkin: () => api.get('/gym/checkin'),
  saveCheckin: (image) => api.put('/gym/checkin', { image }),
  deleteCheckin: () => api.delete('/gym/checkin'),
  activeRun: () => api.get('/gym/runs/active'),
  runs: () => api.get('/gym/runs'),
  progress: () => api.get('/gym/progress'),
  exercises: () => api.get('/gym/exercises'),
  startRun: (sessionId) => api.post('/gym/runs', { session_id: sessionId }),
  updateRun: (id, body) => api.patch(`/gym/runs/${id}`, body),
  deleteRun: (id) => api.delete(`/gym/runs/${id}`),
};

// Finger rehab, inside the gym: the caller's own log. GET returns everything with the sums done.
export const rehabAPI = {
  all: (today) => api.get('/gym/rehab', { params: { today } }),
  saveDay: (date, body) => api.put(`/gym/rehab/days/${date}`, body),
  deleteDay: (date) => api.delete(`/gym/rehab/days/${date}`),
  createSession: (body) => api.post('/gym/rehab/sessions', body),
  updateSession: (id, body) => api.patch(`/gym/rehab/sessions/${id}`, body),
  deleteSession: (id) => api.delete(`/gym/rehab/sessions/${id}`),
  createTest: (body) => api.post('/gym/rehab/tests', body),
  updateTest: (id, body) => api.patch(`/gym/rehab/tests/${id}`, body),
  deleteTest: (id) => api.delete(`/gym/rehab/tests/${id}`),
  createExercise: (body) => api.post('/gym/rehab/exercises', body),
  updateExercise: (id, body) => api.patch(`/gym/rehab/exercises/${id}`, body),
  deleteExercise: (id) => api.delete(`/gym/rehab/exercises/${id}`),
  createBlock: (body) => api.post('/gym/rehab/blocks', body),
  updateBlock: (id, body) => api.patch(`/gym/rehab/blocks/${id}`, body),
  deleteBlock: (id) => api.delete(`/gym/rehab/blocks/${id}`),
};

// Graphics Preview Sync: one per workspace. Start checks both Singular apps first, so allow a moment.
export const previewSyncAPI = {
  status: () => api.get('/tools/preview-sync/status'),
  start: (body) => api.post('/tools/preview-sync/start', body, { timeout: 30000 }),
  stop: () => api.post('/tools/preview-sync/stop', {}),
};

// Fleet: the account's CamNode boxes. Commands are queued and reach a box with
// its next heartbeat (about 2 s), so these return 202 with the queued command.
/** Climbing scoring competitions (routes/scoring.js). The judging itself is on the screens' WebSocket. */
export const scoringAPI = {
  list: () => api.get('/scoring'),
  create: (body) => api.post('/scoring', body),
  get: (id) => api.get(`/scoring/${id}`),
  update: (id, changes) => api.patch(`/scoring/${id}`, changes),
  remove: (id) => api.delete(`/scoring/${id}`),
  setup: (id, body) => api.post(`/scoring/${id}/setup`, body),
  setMode: (id, mode) => api.post(`/scoring/${id}/mode`, { mode }),
  linkCode: (id) => api.post(`/scoring/${id}/link-code`, {}),
  results: (id) => api.get(`/scoring/${id}/results`),
  events: (id) => api.get(`/scoring/${id}/events`),
};

export const fleetAPI = {
  boxes: () => api.get('/fleet/boxes'),
  box: (id) => api.get(`/fleet/boxes/${id}`),
  thumbnail: (id) => api.get(`/fleet/boxes/${id}/thumbnail.jpg`, { responseType: 'blob' }),
  identify: (id) => api.post(`/fleet/boxes/${id}/actions/identify`, {}),
  testTone: (id) => api.post(`/fleet/boxes/${id}/actions/test-tone`, {}),
  restart: (id, service) => api.post(`/fleet/boxes/${id}/actions/restart`, { service }),
  reboot: (id, force = false) => api.post(`/fleet/boxes/${id}/actions/reboot`, force ? { force: true } : {}),
  update: (id, changes) => api.patch(`/fleet/boxes/${id}`, changes),
  remove: (id) => api.delete(`/fleet/boxes/${id}`),
  codes: () => api.get('/fleet/enrolment-codes'),
  createCode: (body) => api.post('/fleet/enrolment-codes', body),
  revokeCode: (id) => api.delete(`/fleet/enrolment-codes/${id}`),
  alerts: (openOnly = false) => api.get('/fleet/alerts', { params: openOnly ? { open: 1 } : {} }),
};

export const timingBridgeAPI = {
  // Reads the timing site (and, for Apex, waits for its first grid), so longer timeouts.
  resolve: (body) => api.post('/tools/timing-bridge/resolve', body, { timeout: 30000 }),
  start: (body) => api.post('/tools/timing-bridge/start', body, { timeout: 30000 }),
  stop: (showId) => api.post('/tools/timing-bridge/stop', { show_id: showId }),
  status: (showId) => api.get('/tools/timing-bridge/status', {
    params: showId ? { show_id: showId } : {},
    // A poll that arrives late is worthless — the next one is already due.
    timeout: 5000,
  }),
  state: (showId) => api.get('/tools/timing-bridge/state', { params: { show_id: showId }, timeout: 5000 }),
  setDriver: (showId, driverNo) => api.post('/tools/timing-bridge/driver', { show_id: showId, driver_no: driverNo }),
  setBattle: (showId, pair) => api.post('/tools/timing-bridge/battle', { show_id: showId, pair }),
  autoBattle: (showId) => api.post('/tools/timing-bridge/battle', { show_id: showId, pair: null, auto: true }),
  setFrozen: (showId, frozen) => api.post('/tools/timing-bridge/freeze', { show_id: showId, frozen }),
  updateConfig: (showId, body) => api.patch('/tools/timing-bridge/config', { show_id: showId, ...body }, { timeout: 30000 }),
  upstream: (showId, params = {}) => api.get('/tools/timing-bridge/upstream', { params: { show_id: showId, ...params } }),
  runs: () => api.get('/tools/timing-bridge/runs'),
  // Re-reading a Speedhive championship or a Sheet can take a few seconds.
  // Omit the championship for all of them.
  reloadChampionship: (showId, clearBanked = false, championship = null) => api.post('/tools/timing-bridge/championship/reload', { show_id: showId, clear_banked: clearBanked, championship }, { timeout: 30000 }),
  bankChampionship: (showId, championship = null) => api.post('/tools/timing-bridge/championship/bank', { show_id: showId, championship }),
  // Listing a Drive folder can take a few seconds.
  checkPhotos: (folder) => api.post('/tools/timing-bridge/photos/check', { folder }, { timeout: 30000 }),
  reloadPhotos: (showId, folder) => api.post('/tools/timing-bridge/photos/reload', folder === undefined ? { show_id: showId } : { show_id: showId, folder }, { timeout: 30000 }),
  recordings: () => api.get('/tools/timing-bridge/recordings'),
  renameRecording: (id, name) => api.patch(`/tools/timing-bridge/recordings/${id}`, { name }),
  deleteRecording: (id) => api.delete(`/tools/timing-bridge/recordings/${id}`),
  startRecording: (showId, name) => api.post('/tools/timing-bridge/recording/start', { show_id: showId, ...(name ? { name } : {}) }),
  // Stopping waits for the last chunk to be stored.
  stopRecording: (showId) => api.post('/tools/timing-bridge/recording/stop', { show_id: showId }, { timeout: 30000 }),
  // Reads a championship and writes a whole meeting: allow a moment.
  simulate: (body) => api.post('/tools/timing-bridge/simulate', body, { timeout: 60000 }),
  startPlayback: (body) => api.post('/tools/timing-bridge/playback/start', body, { timeout: 60000 }),
  controlPlayback: (showId, action, params = {}) => api.post('/tools/timing-bridge/playback/control', { show_id: showId, action, ...params }),
  // The broadcast page: read-only, never affects the streams.
  view: (showId) => api.get('/tools/timing-bridge/view', { params: { show_id: showId }, timeout: 5000 }),
  viewLink: (showId, regenerate = false) => api.post('/tools/timing-bridge/view-link', { show_id: showId, regenerate }),
  unbankChampionship: (showId, championship, bankId) => api.post('/tools/timing-bridge/championship/unbank', { show_id: showId, championship, bank_id: bankId }),
  // Today's points adjustments (a Superpole win, a penalty), and the Superpole points on offer.
  adjustChampionship: (showId, championship, { no, points, reason }) => api.post('/tools/timing-bridge/championship/adjust', { show_id: showId, championship, no, points, reason }),
  removeChampionshipAdjustment: (showId, championship, adjustmentId) => api.post('/tools/timing-bridge/championship/adjust/remove', { show_id: showId, championship, adjustment_id: adjustmentId }),
  answerChampionshipSuggestion: (showId, championship, accept) => api.post('/tools/timing-bridge/championship/suggestion', { show_id: showId, championship, accept }),
};

/**
 * The broadcast timing page by share link, for commentators with no login.
 * Plain fetch, not `api`: the shared client treats a 401 as an expired session
 * and sends the browser to /login, which is wrong for someone who never had one.
 */
export const publicTimingAPI = {
  view: async (token) => {
    const base = import.meta.env.VITE_API_URL || '/api';
    const res = await fetch(`${base}/public/timing/${encodeURIComponent(token)}`, { cache: 'no-store' });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || 'Could not load live timing');
    return data;
  },
};

// OAuth consent screen. Goes through /api so the Vercel rewrite reaches the
// backend; the MCP client itself talks to /oauth/* on the backend directly.
export const oauthAPI = {
  authorizationRequest: (params) => api.get('/oauth/authorization-request', { params }),
  authorize: (body) => api.post('/oauth/authorize', body),
};

// MCP token API — "Connect Claude" in Workspace Settings.
// Deliberately has no MCP tool equivalent: a token must not be able to mint or
// revoke tokens, or scoping would be decorative. See backend/src/mcp/waivers.js.
export const mcpTokensAPI = {
  list: () => api.get('/mcp-tokens'),
  create: (body) => api.post('/mcp-tokens', body),
  revoke: (id) => api.delete(`/mcp-tokens/${id}`),
  // What Claude would be able to see for a given scope set, so an operator can
  // check a token's blast radius before issuing it.
  tools: (scopes) => api.get('/mcp-tokens/tools', { params: { scopes: scopes.join(',') } }),
};

// Billing API. organisationId picks the account; omitted, the server uses the
// account that owns the open workspace.
export const billingAPI = {
  status: (organisationId) => api.get('/billing/status', { params: organisationId ? { organisation_id: organisationId } : {} }),
  createCheckout: (organisationId) => api.post('/billing/create-checkout', { organisation_id: organisationId }),
  portal: (organisationId) => api.post('/billing/portal', { organisation_id: organisationId }),
};

// Admin API (superadmin only)
export const adminAPI = {
  organisations: () => api.get('/admin/organisations'),
  organisation: (id) => api.get(`/admin/organisations/${id}`),
  updateOrganisation: (id, data) => api.patch(`/admin/organisations/${id}`, data),
  mergeOrganisation: (id, intoId, memberRole) =>
    api.post(`/admin/organisations/${id}/merge`, { into_organisation_id: intoId, member_role: memberRole }),
  deleteOrganisation: (id) => api.delete(`/admin/organisations/${id}`),
  addMember: (orgId, userId, role) => api.post(`/admin/organisations/${orgId}/members`, { user_id: userId, role }),
  setMemberRole: (orgId, userId, role) => api.patch(`/admin/organisations/${orgId}/members/${userId}`, { role }),
  removeMember: (orgId, userId) => api.delete(`/admin/organisations/${orgId}/members/${userId}`),
  moveWorkspace: (workspaceId, orgId) => api.post(`/admin/workspaces/${workspaceId}/move`, { organisation_id: orgId }),
  users: (q) => api.get('/admin/users', { params: q ? { q } : {} }),
  user: (id) => api.get(`/admin/users/${id}`),
  updateUser: (id, data) => api.patch(`/admin/users/${id}`, data),
  mergeUser: (id, intoId) => api.post(`/admin/users/${id}/merge`, { into_user_id: intoId }),
  deleteUser: (id) => api.delete(`/admin/users/${id}`),
};

export default api;
